// D119.13 - Accounts "Margin by vessel": sell / cost / margin in TT$ per vessel for a date range; a
// vessel opens to its deliveries (one client invoice each), each linking to the invoice. Desktop only.
// The server owns every figure (utils/clientInvoiceMargin); this only draws them. A blank figure is
// "--", never 0.00: an invoice with no cost, or no pinned rate, says so instead of looking profitable.
// D119.14: Sell is net of credit notes; a line with no cost is left out of the margin and the Margin %.
import React, { Fragment, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import DateInput from '../../components/DateInput';
import StatusBadge from '../../components/StatusBadge';
import Button from '../../components/ui/Button';
import SearchInput from '../../components/ui/SearchInput';
import { downloadObjectsCsv } from '../../utils/downloadCsv';
import { useLatestRequest } from '../../hooks/useLatestRequest';
import { fetchMarginByVessel, type MarginByVesselReport, type MarginFigures } from '../../lib/clientInvoiceMarginApi';
import { formatDate } from '../../utils/dateFormat';
import { moneyOrBlank, pctOrBlank } from '../clientInvoice/profitabilityRows';
import { browserToday } from '../vendorInvoices/paymentOnReceiptForm';

type Props = { onOpenInvoice: (invoiceId: string) => void };

const ttd = (v: number | null | undefined) => moneyOrBlank(v, 'TTD');
const tone = (v: number | null | undefined) => (typeof v === 'number' && v < 0 ? 'text-rose-600' : 'text-slate-900');

const SELL_HINT = 'Everything billed, net of credit notes (lines with no cost included).';
const MARGIN_PCT_HINT = 'Margin over the sale of the lines that have a cost.';
const noCostLines = (n: number) => `${n} line${n === 1 ? '' : 's'} with no cost, left out of the margin`;

/** Why a line of figures is not the whole story; blank when it is complete. */
export function incompleteNote(f: Pick<MarginFigures, 'uncostedLines' | 'uncostedInvoices' | 'unconvertedInvoices' | 'unbilledInvoices'>): string {
  const parts: string[] = [];
  if (f.uncostedLines > 0) parts.push(noCostLines(f.uncostedLines));
  if (f.uncostedInvoices > 0) parts.push(`${f.uncostedInvoices} deliver${f.uncostedInvoices === 1 ? 'y' : 'ies'} with no cost at all`);
  if (f.unconvertedInvoices > 0) parts.push(`${f.unconvertedInvoices} deliver${f.unconvertedInvoices === 1 ? 'y' : 'ies'} with no TT$ rate`);
  if (f.unbilledInvoices > 0) parts.push(`${f.unbilledInvoices} deliver${f.unbilledInvoices === 1 ? 'y' : 'ies'} with nothing billed`);
  return parts.join(' · ');
}

function Flag({ figures }: { figures: MarginFigures }) {
  if (!figures.incomplete) return null;
  return (
    <span
      className="ml-2 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
      title="A delivery with no cost at all, no TT$ rate, or nothing billed, is left out of the margin. In a delivery with some cost, a line with no cost is left out of the margin too; its sale still shows in Sell. It is not the whole picture."
    >
      Incomplete: {incompleteNote(figures)}
    </span>
  );
}

export function MarginByVesselPanel({ onOpenInvoice }: Props) {
  const today = browserToday();
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState(today);
  const [report, setReport] = useState<MarginByVesselReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const beginRequest = useLatestRequest();

  useEffect(() => {
    const isCurrent = beginRequest();
    setLoading(true);
    setFailed(false);
    fetchMarginByVessel({ from, to })
      .then((res) => {
        if (!isCurrent()) return;
        setReport(res);
        setOpen(null);
      })
      .catch(() => {
        if (!isCurrent()) return;
        setReport(null);
        setFailed(true);
      })
      .finally(() => {
        if (isCurrent()) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  // The same toolbar as the other Accounts tabs: a search box and Export CSV (UI-05).
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();
  const vessels = (report?.vessels ?? []).filter((v) => !term || v.vessel.toLowerCase().includes(term));
  return (
    <>
    {/* The same strip as the other Accounts tabs, under the tab switcher (UI-05). */}
    <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-2 sm:px-8">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <SearchInput value={search} onChange={setSearch} placeholder="Search here" className="w-64" />
        <Button
          variant="primary"
          disabled={vessels.length === 0}
          onClick={() =>
            downloadObjectsCsv(
              'margin-by-vessel.csv',
              vessels.map((v) => ({
                Vessel: v.vessel,
                Deliveries: v.deliveries,
                'Sell TT$': v.sellTtd ?? '',
                'Cost TT$': v.costTtd ?? '',
                'Margin TT$': v.marginTtd ?? '',
                'Margin %': v.marginPct ?? '',
              }))
            )
          }
          icon={<Download aria-hidden="true" className="h-4 w-4" />}
        >
          Export CSV
        </Button>
      </div>
    </div>
    <div className="flex-1 space-y-4 overflow-auto px-8 py-4">
      <div className="flex flex-wrap items-end gap-4">
        <label className="text-xs text-slate-500">
          From
          <DateInput value={from} onChange={setFrom} validity="filter" max={to || undefined} aria-label="From date" className="mt-1 block h-9 w-40 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20" />
        </label>
        <label className="text-xs text-slate-500">
          To
          <DateInput value={to} onChange={setTo} validity="filter" min={from || undefined} aria-label="To date" className="mt-1 block h-9 w-40 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20" />
        </label>
        <p className="max-w-xl text-xs text-slate-500">
          Selling against total cost per delivery, in TT$. A US$ delivery is converted at the rate pinned on its own invoice.
          Credit notes are taken off each delivery&apos;s sale. A line with no cost is left out of the margin, and Margin % is
          over the sale of the lines that have a cost. Cancelled invoices are left out.
        </p>
      </div>

      {failed ? (
        <p role="alert" className="text-sm text-rose-700">The margin report could not be loaded. Check your connection and try again.</p>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="data-table data-table-dense">
          <thead className="sticky top-0 z-10">
            <tr>
              <th>Vessel</th>
              <th className="whitespace-nowrap text-right">Deliveries</th>
              <th className="whitespace-nowrap text-right" title={SELL_HINT}>Sell TT$</th>
              <th className="whitespace-nowrap text-right">Cost TT$</th>
              <th className="whitespace-nowrap text-right">Margin TT$</th>
              <th className="whitespace-nowrap text-right" title={MARGIN_PCT_HINT}>Margin %</th>
            </tr>
          </thead>
          <tbody>
            {!loading && !failed && vessels.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-400">No deliveries in this period.</td>
              </tr>
            )}
            {vessels.map((v) => {
              const isOpen = open === v.vessel;
              return (
                <Fragment key={v.vessel}>
                  <tr className="cursor-pointer" onClick={() => setOpen(isOpen ? null : v.vessel)}>
                    <td className="font-medium text-slate-900">
                      <button type="button" aria-expanded={isOpen} className="inline-flex items-center gap-1 text-left" onClick={(e) => { e.stopPropagation(); setOpen(isOpen ? null : v.vessel); }}>
                        {isOpen ? <ChevronDown aria-hidden="true" className="h-4 w-4" /> : <ChevronRight aria-hidden="true" className="h-4 w-4" />}
                        {v.vessel}
                      </button>
                      <Flag figures={v} />
                    </td>
                    <td className="whitespace-nowrap text-right tabular-nums">{v.deliveries}</td>
                    <td className="whitespace-nowrap text-right tabular-nums">{ttd(v.sellTtd)}</td>
                    <td className="whitespace-nowrap text-right tabular-nums">{ttd(v.costTtd)}</td>
                    <td className={`whitespace-nowrap text-right font-semibold tabular-nums ${tone(v.marginTtd)}`}>{ttd(v.marginTtd)}</td>
                    <td className={`whitespace-nowrap text-right tabular-nums ${tone(v.marginPct)}`}>{pctOrBlank(v.marginPct)}</td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={6} className="bg-slate-50 p-0">
                        <table className="w-full text-xs" aria-label={`${v.vessel} deliveries`}>
                          <thead>
                            <tr className="text-left font-semibold text-slate-600">
                              <th className="px-3 py-2">Invoice #</th>
                              <th className="px-3 py-2">Date</th>
                              <th className="px-3 py-2">Status</th>
                              <th className="px-3 py-2 text-right" title={SELL_HINT}>Sell TT$</th>
                              <th className="px-3 py-2 text-right">Cost TT$</th>
                              <th className="px-3 py-2 text-right">Margin TT$</th>
                              <th className="px-3 py-2 text-right" title={MARGIN_PCT_HINT}>Margin %</th>
                              <th className="px-3 py-2">Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {v.invoices.map((r) => (
                              <tr key={r.id} className="cursor-pointer border-t border-slate-200 hover:bg-white" onClick={() => onOpenInvoice(r.id)}>
                                <td className="px-3 py-2 font-medium text-primary-600">
                                  <button type="button" onClick={(e) => { e.stopPropagation(); onOpenInvoice(r.id); }}>{r.invoiceNumber || '--'}</button>
                                </td>
                                <td className="px-3 py-2 text-slate-500">{formatDate(r.invoiceDate, '--')}</td>
                                <td className="px-3 py-2"><StatusBadge status={r.status || 'draft'} /></td>
                                <td className="px-3 py-2 text-right tabular-nums">{ttd(r.sellTtd)}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{ttd(r.costTtd)}</td>
                                <td className={`px-3 py-2 text-right font-semibold tabular-nums ${tone(r.marginTtd)}`}>{ttd(r.marginTtd)}</td>
                                <td className={`px-3 py-2 text-right tabular-nums ${tone(r.marginPct)}`}>{pctOrBlank(r.marginPct)}</td>
                                <td className="px-3 py-2 text-amber-800">
                                  {r.currency !== 'TTD' && !r.usdToTtd
                                    ? 'No TT$ rate pinned'
                                    : r.nothingBilled
                                      ? 'Nothing billed yet'
                                      : !r.hasCostData
                                      ? 'No cost yet'
                                      : r.uncostedLines > 0
                                        ? noCostLines(r.uncostedLines)
                                        : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          {report && vessels.length > 0 && (
            <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
              <tr>
                <td className="px-3 py-2.5">
                  All vessels
                  <Flag figures={report.totals} />
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">{report.totals.deliveries}</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">{ttd(report.totals.sellTtd)}</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">{ttd(report.totals.costTtd)}</td>
                <td className={`whitespace-nowrap px-3 py-2.5 text-right tabular-nums ${tone(report.totals.marginTtd)}`}>{ttd(report.totals.marginTtd)}</td>
                <td className={`whitespace-nowrap px-3 py-2.5 text-right tabular-nums ${tone(report.totals.marginPct)}`}>{pctOrBlank(report.totals.marginPct)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
    </>
  );
}
